import { DialogDisclosure, Form } from "@ariakit/react";
import {
  DialogTrigger,
  FormSubmitButton,
  getSubmitFailureMessage,
  LoadingIndicator,
  SubmitErrorAlert,
  useCurrentUser,
  useForm,
} from "@indietabletop/appkit";
import { interactiveText } from "@indietabletop/appkit/common.css.ts";
import { Link, useSearchParams } from "wouter";
import { client, useInvokeClient } from "../../client.ts";
import { hrefs, type BuyParams } from "../../hrefs.ts";
import { trackEvent } from "../../lib/fathom.tsx";
import {
  DownloadButton,
  DownloadSection,
} from "../DownloadSection/DownloadSection.tsx";
import { FailureView } from "../failures/FailureView.tsx";
import { NonFormDialog } from "../FormDialog/FormDialog.tsx";
import { LoadingView } from "../LoadingView/LoadingView.tsx";
import { Page } from "../Page/Page.tsx";
import { Emphasis } from "./Emphasis.tsx";
import { FeaturesOverview } from "./FeaturesOverview.tsx";
import { accountDialog, buy, note } from "./style.css.ts";

const BUY = "Buy £20";

function AccountNeededDialog() {
  return (
    <NonFormDialog heading="Account Needed" overline="Hold up!">
      <p className={accountDialog.info}>
        To unlock <Emphasis>in-app features</Emphasis> and to allow for{" "}
        <Emphasis>easy downloads</Emphasis>, we need an account to associate
        with your purchase.
      </p>

      <div className={accountDialog.actions}>
        <Link
          href={hrefs.login({
            redirectTo: hrefs.buy({ note: "backToIt" }),
          })}
          className={accountDialog.login}
        >
          Log in
        </Link>

        <Link
          href={hrefs.join({
            redirectTo: hrefs.buy({ note: "backToIt" }),
          })}
          className={accountDialog.join}
        >
          Join
        </Link>
      </div>
    </NonFormDialog>
  );
}

function QuickStartRulesButton() {
  return (
    <Link className={buy.demo} href={hrefs.qsr()}>
      <div>Quick Start Rules</div>
      <div className={buy.demoInfo}>Try for free!</div>
    </Link>
  );
}

function BuyButton() {
  const { form, submitName } = useForm({
    defaultValues: {},
    async onSubmit() {
      const result = await client.startCheckoutSession({
        products: [{ code: "spacegits", quantity: 1 }],
        successUrl: `${window.location.origin}/checkout/sessions/{CHECKOUT_SESSION_ID}`,
        cancelUrl: window.location.toString(),
      });

      if (result.isFailure) {
        return result.mapFailure((failure) => getSubmitFailureMessage(failure));
      }

      // Full redirect to redirect URL.
      window.location.assign(result.value.redirectTo);
      return result;
    },
  });

  return (
    <Form store={form} className={buy.form}>
      <FormSubmitButton
        loading={<LoadingIndicator />}
        className={buy.button}
        onClick={() => void trackEvent("buy")}
      >
        {BUY}
      </FormSubmitButton>

      <div className={buy.alreadyOwn}>
        <SubmitErrorAlert name={submitName} />

        <Link
          href="~/docs/help/accessing-owned-content"
          className={interactiveText}
        >
          I already own Space Gits
        </Link>
      </div>
    </Form>
  );
}

function Note(props: {
  heading: string;
  text: string;
  param: BuyParams["note"];
}) {
  const [params] = useSearchParams();
  const currentParam = params.get("note") as BuyParams["note"] | null;

  if (currentParam !== props.param) {
    return null;
  }

  return (
    <div className={note.container}>
      <div className={note.heading}>{props.heading}</div>
      <div className={note.text}>{props.text}</div>
    </div>
  );
}

function BackToItNote() {
  return (
    <Note
      param="backToIt"
      heading="Account Sorted!"
      text="Shall we get back to it?"
    />
  );
}

function LoginNeededNote() {
  return (
    <Note
      param="loginNeeded"
      heading="Login Needed!"
      text="Please log in to download."
    />
  );
}

function AlreadyOwnNote() {
  return (
    <div className={buy.alreadyOwn}>
      <Link
        href="~/docs/help/accessing-owned-content"
        className={interactiveText}
      >
        I already own Space Gits
      </Link>
    </div>
  );
}

export function RulesPage() {
  const currentUser = useCurrentUser();

  const [result] = useInvokeClient("getOwnedProduct", ["spacegits"]);

  if (result.isPending) {
    return (
      <Page title="Rules" backHref="~/" centerChildren>
        <LoadingView />
      </Page>
    );
  }

  if (result.isFailure) {
    if (result.failure.type === "API_ERROR") {
      // The no currentUser case is possible in case the user is logged into
      // ITC via a domain-wide cookie, but they have not yet logged into the
      // Space Gits app.
      if (result.failure.code === 401 || !currentUser) {
        return (
          <Page title="Rules" backHref="~/">
            <DownloadSection
              note={<LoginNeededNote />}
              action={
                <>
                  <DialogTrigger>
                    <AccountNeededDialog />
                    <DialogDisclosure className={buy.button}>
                      {BUY}
                    </DialogDisclosure>
                  </DialogTrigger>

                  <QuickStartRulesButton />

                  <AlreadyOwnNote />
                </>
              }
            />
            <FeaturesOverview />
          </Page>
        );
      }

      if (result.failure.code === 403) {
        return (
          <Page title="Rules" backHref="~/">
            <DownloadSection
              note={<BackToItNote />}
              action={
                <>
                  <BuyButton />
                  <QuickStartRulesButton />
                  <AlreadyOwnNote />
                </>
              }
            />
            <FeaturesOverview />
          </Page>
        );
      }
    }

    return (
      <Page title="Rules" backHref="~/" centerChildren>
        <FailureView failure={result.failure} />
      </Page>
    );
  }

  // This shouldn't happen in practice, as this particular endpoint errors out
  // if a product link fails to generate.
  if (!result.value.downloadUrl) {
    return (
      <Page title="Rules" backHref="~/" centerChildren>
        <FailureView failure={{ type: "UNKNOWN_ERROR" }} />
      </Page>
    );
  }

  return (
    <Page title="Rules" backHref="~/">
      <DownloadSection
        note={<BackToItNote />}
        action={
          <>
            <DownloadButton href={result.value.downloadUrl}>
              Download
            </DownloadButton>

            <div className={buy.alreadyOwn}>
              ...or get stuck in with in-app
              <br />
              <Link href={hrefs.qsr()} className={interactiveText}>
                Quick Start Rules
              </Link>
              .
            </div>
          </>
        }
      />
    </Page>
  );
}
